import { Alert, Button, Checkbox, Container, FormControlLabel, FormGroup, Grid, Paper, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserServiceInstance from '../../services/UserService';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SearchIcon from '@mui/icons-material/Search';
import ITagRegistration from '../../models/ITagRegistration';
import { useNavigate } from 'react-router-dom';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';
import IUnlinkedTagRider from '../../models/IUnlinkedTagRider';

const useStyles = makeStyles((theme) => ({
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
    },
    appBarSpacer: theme.mixins.toolbar,
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    }
}));

function RegisterTag() {
    const [tagRegistration, setTagRegistration] = useState<ITagRegistration>({
        tagNumber: '',
        pin: ''
    });
    const [unlinkedRiders, setUnlinkedRiders] = useState<IUnlinkedTagRider[]>([]);
    const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
    const [searchingEvents, setSearchingEvents] = useState(false);
    const [searchError, setSearchError] = useState<string>();
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const classes = useStyles();

    const registerTag = async () => {
        await UserServiceInstance.registerTag({
            ...tagRegistration,
            events: selectedEvents,
        });
        navigate('/user/tags');
    }

    const searchUnlinkedRiders = async () => {
        setSearchingEvents(true);
        setSearchError(undefined);
        try {
            const riders = await UserServiceInstance.getUnlinkedTagRiders(tagRegistration.tagNumber, tagRegistration.pin);
            setUnlinkedRiders(riders);
            setSelectedEvents([]);
        } catch (error) {
            console.error(error);
            setUnlinkedRiders([]);
            setSelectedEvents([]);
            setSearchError(error instanceof Error ? error.message : t('registerTag.searchError').toString());
        } finally {
            setSearchingEvents(false);
        }
    }

    const toggleEventSelection = (eventId: string) => {
        setSelectedEvents((current) =>
            current.includes(eventId)
                ? current.filter((id) => id !== eventId)
                : [...current, eventId]
        );
    }

    const formatEventLabel = (rider: IUnlinkedTagRider) => {
        const eventDate = rider.eventDate ? new Date(rider.eventDate).toLocaleDateString() : '';
        return `${rider.eventName} - ${rider.circuitName} (${eventDate}) - ${rider.participantName}`;
    }

    return (
        <main className={classes.content}>
            <div className={classes.appBarSpacer} />
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={{ [t('userTags.title')]: '/user/tags' }} current={t('registerTag.registerTag')} />
                    </Grid>
                    <Grid item xs={12}>
                        <Paper className={classes.paper}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        id="tagNumber"
                                        required
                                        label={t("registerTag.tagNumber")}
                                        fullWidth
                                        type="text"
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                        value={tagRegistration.tagNumber}
                                        onChange={e => {
                                            setTagRegistration({ ...tagRegistration, tagNumber: e.target.value });
                                            setUnlinkedRiders([]);
                                            setSelectedEvents([]);
                                            setSearchError(undefined);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        id="pin"
                                        required
                                        label={t("registerTag.PIN")}
                                        fullWidth
                                        type="text"
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                        value={tagRegistration.pin}
                                        onChange={e => {
                                            setTagRegistration({ ...tagRegistration, pin: e.target.value });
                                            setSearchError(undefined);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={searchUnlinkedRiders}
                                        startIcon={<SearchIcon />}
                                        disabled={!tagRegistration.tagNumber || !tagRegistration.pin || searchingEvents}
                                    >
                                        {t('registerTag.searchEvents')}
                                    </Button>
                                </Grid>
                                {searchError ? (
                                    <Grid item xs={12}>
                                        <Alert severity="error">{searchError}</Alert>
                                    </Grid>
                                ) : null}
                                {unlinkedRiders.length > 0 ? (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1">{t('registerTag.selectEvents')}</Typography>
                                        <FormGroup>
                                            {unlinkedRiders.map((rider) => (
                                                <FormControlLabel
                                                    key={rider.raceId}
                                                    control={
                                                        <Checkbox
                                                            checked={selectedEvents.includes(rider.raceId)}
                                                            onChange={() => toggleEventSelection(rider.raceId)}
                                                        />
                                                    }
                                                    label={formatEventLabel(rider)}
                                                />
                                            ))}
                                        </FormGroup>
                                    </Grid>
                                ) : null}
                                {searchingEvents ? (
                                    <Grid item xs={12}>
                                        <Typography variant="body2">{t('registerTag.searchingEvents')}</Typography>
                                    </Grid>
                                ) : null}
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                                        {t('registerTag.selectedEventsCount', { count: selectedEvents.length })}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={registerTag}
                                        startIcon={<LockOpenIcon />}
                                        disabled={!tagRegistration.tagNumber || !tagRegistration.pin}
                                    >{t("registerTag.registerTag")}</Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </main>
    );

}

export default RegisterTag;
